import React, { useState, useEffect } from 'react';
import Helmet from 'react-helmet';

import Moment from 'moment-timezone';

import Configure from '@/components/hierarchical-report/configure';
import PrivateRoute from '@Components/app-layout/PrivateRoute';
import sweepWinnerStore from 'modules/report/stores/sweepWinner';
import appStore from 'stores/app';

import Summary from '../losses/components/Summary';

import Header from './components/Header'
import ReportCsv from './components/ReportCsv';
import SweepWinnerTable from './components/SweepWinnerTable';
import WinnerSidebar from './components/sidebar/WinnerSidebar';
import { formatters } from './components/utils';
import { useSweepWinnerReportStore, SweepWinnerReportProvider } from './store'
import { ReportBody } from '@/components/reports/body';

function SweepWinnerLayout() {
  const [state, setState] = useState(null)
  const [data, setData] = useState(null)

  useEffect(() => {
    if (state) {
      loadData()
    }
  }, [window.location.pathname])

  const defaultParams = {
    filterOpen: false,
    searchTerm: 'sweepWinner',
    isExport: false,
    params: {
      siteId: appStore.currentSiteID,
      startDate: Moment.tz(appStore.timezone).startOf("day"),
      endDate: Moment.tz(appStore.timezone).endOf("day"),
      filterby: {},
    },
  }

  useEffect(() => {
    loadState()
  }, [])

  function loadState() {
    setState(defaultParams)
    loadData(defaultParams)
  }

  async function loadData(data) {
    const value = data ? data.hasOwnProperty('params') ? data : state : state
    await sweepWinnerStore.fetchData(value.params)
    setData(sweepWinnerStore.collection.data)
    setState(value)
  }

  function handleClickExport() {
    setState({ ...state, isExport: true });
    setTimeout(() => setState({ ...state, isExport: false }), 1000)
  }

  function handleChangeDates({ startDate, endDate }) {
    const newState = {
      ...state,
      params: {
        ...state.params,
        startDate,
        endDate,
      },
    }

    setState(newState)
    if (startDate && endDate) {
      loadData(newState)
    }
  }

  function handleChangeConfigure(value) {
    if (!value.filterby) return

    value.filterby = Object.keys(value.filterby).reduce((filterby, field) => {
      const items = value.filterby[field].items.map(item => item.getValue())
      if (items.length > 0) filterby = { ...filterby, ...{ [field]: items } }
      return filterby
    }, {})

    setState(prevState => {
      prevState.params.filterby = value.filterby || {}
      return prevState
    })

    loadData()
  }

  function handleChangeSelectConfigure(value) {
    setState(prevState => {
      prevState.params.filterby = value.filterby || {}
      return prevState
    })

    loadData();
  }

  function ConfigureSidebar() {
    const configure = {
      filterby: {
        options: [
          {
            value: 'sweep',
            get label() {
              return <formatters.Loadable isLoading={sweepWinnerStore.sweep.isLoading}>Sweep</formatters.Loadable>
            },
            options: sweepWinnerStore.sweep.fetchData,
          },
          {
            value: 'contest',
            get label() {
              return <formatters.Loadable isLoading={sweepWinnerStore.contest.isLoading}>Contest</formatters.Loadable>
            },
            options: sweepWinnerStore.contest.fetchData,
          },
          {
            value: 'contestStatus',
            get label() {
              return <formatters.Loadable>Contest Status</formatters.Loadable>
            },
            options: async () => [
              { value: "active", label: "Active", getLabel: () => "Active", getValue: () => "active" },
              { value: "finished", label: "Finished", getLabel: () => "Finished", getValue: () => "finished" },
            ],
          },
          {
            value: 'winnerStatus',
            get label() {
              return <formatters.Loadable>Winner Status</formatters.Loadable>
            },
            options: async () => [
              {
                value: "fulfilled",
                label: "Fulfilled",
                getLabel: () => 'Fulfilled',
                getValue: () => 'fulfilled',
              },
              {
                value: "pending_contact",
                label: "Pending Contact",
                getLabel: () => 'Pending Contact',
                getValue: () => 'pending_contact',
              },
              {
                value: "pending_verification",
                label: "Pending Verification",
                getLabel: () => 'Pending Verification',
                getValue: () => 'pending_verification',
              },
              {
                value: "forfeited",
                label: "Forfeited",
                getLabel: () => 'Forfeited',
                getValue: () => 'forfeited',
              },
            ],
          },

        ],
      },
    }

    const store = useSweepWinnerReportStore()
    const { report: { useLoad }, reportView } = store
    useLoad()

    const { filterby } = state.params

    return <Configure
      key='filter'
      store={reportView}
      configure={configure}
      onApply={(handleChangeConfigure)}
      onSelect={handleChangeSelectConfigure}
      value={{ filterby }}
    />
  }

  function SummaryCards() {
    const summaryCards = [
      {
        title: "Total Sweepstakes",
        get value() {
          return sweepWinnerStore.sweeps
        },
      },
      {
        title: "Total Contests",
        get value() {
          return sweepWinnerStore.contests
        },
      },
      {
        title: "Winners",
        get value() {
          return sweepWinnerStore.winners
        },
      },
    ]

    return <Summary
      key='summary'
      cards={summaryCards}
      isLoading={sweepWinnerStore.isLoading}
    />
  }

  return (
    <>
      {state && <SweepWinnerReportProvider>
        <Helmet title="Sweep Winner" />
        <Header
          onChangeDates={handleChangeDates}
          onClickExport={handleClickExport}
          dates={{ startDate: state.params.startDate, endDate: state.params.endDate }}
          sync={loadData}
          isLoading={sweepWinnerStore.isLoading}
        >
          <ConfigureSidebar />
        </Header>
        <SummaryCards />
        {state.isExport && <ReportCsv data={data} />}
        <ReportBody hasSummaryCards loading={sweepWinnerStore.isLoading}>
          <SweepWinnerTable data={data} />
        </ReportBody>
        <PrivateRoute path="/reports/sweep-winner/:subscriptionId/:contestWinnerId/winner" component={WinnerSidebar} />
      </SweepWinnerReportProvider>}
    </>

  )
}

export default SweepWinnerLayout;
